import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

const EMPTY_FORM = {
  name: '', email: '', employee_id: '', department: '', masterPersKey: '',
};

function CardholderDialog({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  useEffect(() => {
    if (!open) return;
    setForm(initial
      ? {
          name:          initial.name || '',
          email:         initial.email || '',
          employee_id:   initial.employee_id || '',
          department:    initial.department || '',
          masterPersKey: initial.masterPersKey || '',
        }
      : EMPTY_FORM);
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Add'} Cardholder</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e=>set('name',e.target.value)} className="mt-1 h-8 text-sm" data-testid="cardholder-name-input" /></div>
          <div><Label className="text-xs">Email *</Label><Input value={form.email} onChange={e=>set('email',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Employee ID</Label><Input value={form.employee_id} onChange={e=>set('employee_id',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Department</Label><Input value={form.department} onChange={e=>set('department',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div>
            <Label className="text-xs">masterPersKey (CRM link)</Label>
            <Input value={form.masterPersKey}
                   onChange={e=>set('masterPersKey', e.target.value.trim())}
                   className="mt-1 h-8 text-sm font-mono"
                   placeholder="e.g. PGP-12345"
                   data-testid="cardholder-mpk-input" />
            <p className="text-[10px] text-slate-400 mt-1">
              Optional. When the cardholder signs in via Alchemy SSO, their
              masterPersKey from the CRM is matched against this field to link
              the SSO user to this cardholder profile.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)} data-testid="cardholder-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CardholdersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await API.get('/cardholders'); setItems(r.data); }
    catch(e) { toast.error('Failed to load cardholders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) { await API.put(`/cardholders/${editing.id}`, form); toast.success('Updated'); }
      else { await API.post('/cardholders', form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/cardholders/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  const filtered = items.filter(i => {
    if (!search) return true;
    const q = search.toLowerCase();
    return i.name?.toLowerCase().includes(q)
        || i.email?.toLowerCase().includes(q)
        || i.masterPersKey?.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Cardholders</h1>
        <div className="flex gap-2">
          <Input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} className="h-8 w-48 text-sm" data-testid="cardholder-search" />
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-cardholder-btn">
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="cardholders-table">
          <thead><tr>
            <th>Name</th><th>Email</th><th>Employee ID</th><th>masterPersKey</th><th>Department</th><th>Status</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400"><RefreshCw size={16} className="animate-spin inline" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No cardholders found</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td className="text-slate-500">{c.email}</td>
                <td className="font-mono text-xs">{c.employee_id || '—'}</td>
                <td className="font-mono text-xs">{c.masterPersKey || <span className="text-slate-300">—</span>}</td>
                <td>{c.department || '—'}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full border ${c.is_active !== false ? 'chip-approved' : 'chip-rejected'}`}>{c.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                <td className="text-right">
                  <button onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1 text-slate-400 hover:text-emerald-600 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => setDeleteId(c.id)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CardholderDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Cardholder?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
